// スポット通常入札 一括作成 (AC-005 / AC-005a) の生成ロジック (= Phase 1 の frontend mock)。
//
// 「各エリア・各コマの調達必要量 (需給ギャップ) × 目標カバー率% を数量として、複数コマ ×
// 複数エリアに入札を一括生成」する (機能詳細3 / AC-005a、案B)。数量の分母はエリア別・コマ別
// の need であり、複数エリアを合算しない (合算するとエリア間で必要量が偏った構成でカバー率が
// 実態と乖離するため。AC-016a と同趣旨)。買いは不足コマ (need>0)、売りは余剰コマ (need<0) の
// みを対象とする。「既存入札の扱い」が「追加」の場合は既存入札量 (最終結果になっていない札と
// 約定量。 下の `selectBulkAddExistingVolumes`) を差し引いた不足分のみ生成する。業務ロジックの正本は mock-app `JepxSpotBid.razor` `GenerateBulkBidCandidates()`。
//
// TODO(business-logic): 本来この need→札の算出も、「追加」 で差し引く既存入札量の母集合も、
// 下の Step2 プレビュー行 (数量 + 入札制限判定) も backend が計算して返すべき。
// 本実装はこの原則からの逸脱で、理由は:
//   - 一括生成 (need × カバー率 → 札) を返す read RPC が proto に存在しない
//     (`proto/bff/` に bulk / preview 系 RPC 無し。 供給されるのは
//     `supply_demand_gap_service` の need 値と `bid_limit_service` の制限値だけ)
//   - 入札制限の判定を返す RPC も存在せず、backend の判定 (`ensureWithinBidLimits`) は
//     DRAFT 離脱時 (承認申請 / 提出) にしか実行されない = プレビュー段階で叩ける口が無い
// 解消には proto (新 read RPC) + BFF handler + backend usecase の追加が要る。 それまでは
// frontend で暫定計算し、確定時は既存の書込 RPC (Create / 全置換 Update) で永続化する。
// 判定結果は原則として表示のみに使い、書込の可否ゲートには使わない (= 下の
// `buildBulkPreviewRows` godoc)。 例外は 数量指定モード (AC-BSP-005c) の 上限数量(1札)
// 超過で、これだけは backend の `SpotBidUsecase.Create` が同じ条件で必ず拒否するため
// 確定ボタンを止める (`BulkCreateModal` の `blockingRowCount`、#8324 の EN 暫定判断)。
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import type { BiddingBlockApprovalRequest } from '@ebalance/proto/bffuser/v1/bidding_block_model_pb'

import { isUnknownIssuerLicenseType } from '~/models/bidIssuerType'
import type { BidLimits } from '~/models/bidLimits'
import { checkBidLimit, resolveAreaSideLimit } from '~/models/bidLimits'
import type {
  FeeAdjustmentViolation,
  FeeAutoConsiderConfirmed,
} from '~/models/feeAutoConsiderPreview'
import { BULK_ADD_COUNTED_CATEGORIES } from '~/pages/SpotBidding/models/bidStatusCategory'
import { expandBlockLegSlots } from '~/pages/SpotBidding/models/blockBidLegSlots'
import { deriveBlockBidStatusCategory } from '~/pages/SpotBidding/models/blockBidStatusCategory'
import type { SpotBidRow } from '~/pages/SpotBidding/models/spotBidRow'
import { deriveSpotBidStatusCategory } from '~/pages/SpotBidding/models/spotBidStatusCategory'
import type { VolumeTotalViolation } from '~/pages/SpotBidding/models/spotBidValidation'
import {
  detectStairsExcesses,
  detectVolumeTotalViolations,
} from '~/pages/SpotBidding/models/spotBidValidation'

/**
 * 数量の決め方 (AC-BSP-005 の「数量入力モード」)。
 *
 * - `coverage`: 目標カバー率 (AC-BSP-005a)。各エリア・各コマの需給ギャップ ×
 *   カバー率% を数量にする。買いは不足コマ・売りは余剰コマのみ対象。複数エリア可。
 * - `fixed`: 数量指定 (AC-BSP-005c)。対象コマ範囲の全コマへ同一 MW を、需給
 *   ギャップの方向とは無関係にユーザー指定方向で一律発注する。**単一エリア限定**
 *   (複数エリアへ同時に大量発注する事故を防ぐため)。
 *
 * 業務ロジックの正本は mock-app `JepxSpotBid.razor` の `InputMode` ("gap"/"fixed")。
 */
export type BulkVolumeMode = 'coverage' | 'fixed'

/**
 * 一括作成の「対象エリア」 1 候補 (= checkbox 1 個ぶん)。
 *
 * 母集合 (= 表示するエリア) と選択可否を**同じ 1 件**に持たせる。 集合を
 * 「表示する母集合」 と「モード別に選択できる集合」 の 3 本に分けて渡す形は、
 * 「可否集合は母集合の部分集合」 という invariant が型に現れず、 母集合の外の
 * エリアが可否集合に混じると checkbox にも「選択できないエリアがある」 注記にも
 * 出ないまま生成対象から silent に落ちた (2026-09-10 レビュー指摘)。
 */
export type BulkAreaOption = {
  code: AreaCode
  /**
   * 業務エリア設定に含まれないか。 含まれないエリアは checkbox に残して選べなくし
   * 「業務エリア対象外」 を添える (AC-BSP-005b)。 どちらのモードでも選択可否は false。
   */
  outsideBusinessAreas: boolean
  /**
   * 目標カバー率モード (AC-BSP-005a) で選択できるか
   * (= 調達必要量を取得できている ∩ 取引可能)。 need Map に無いエリアは
   * [[buildBulkSpotBids]] が黙って skip する (= 札が silent に欠落) ため、
   * 取得できていないエリアは選択自体を封じる。
   */
  selectableInCoverageMode: boolean
  /**
   * 数量指定モード (AC-BSP-005c) で選択できるか (= 取引可能。 need は参照しない
   * ので、 need を取得できなくても一括作成できる)。
   */
  selectableInFixedMode: boolean
}

/**
 * 目標カバー率モード (AC-BSP-005a) の数量指定。
 */
type BulkCoverageVolume = {
  volumeMode: 'coverage'
  /** 目標カバー率 (%、 1〜200 の整数)。 */
  coverageRatePercent: number
}

/**
 * 数量指定モード (AC-BSP-005c) の数量指定。
 */
type BulkFixedVolume = {
  volumeMode: 'fixed'
  /** 1 コマあたりの発注量 (MW、 0.1〜9999.9 / 0.1 MW 単位)。 */
  fixedVolumeMw: number
}

/** 一括作成フォームの確定値のうち、 数量の決め方に依らない共通部分。 */
type BulkCreateCommonParams = {
  /** 売/買。 */
  side: Side
  /** 入札価格 (円/kWh、 文字列)。 */
  priceYenPerKwh: string
  /** コマ範囲 (1〜48、 inclusive)。 */
  slotFrom: number
  slotTo: number
  /** 対象エリア (複数選択)。 */
  areaCodes: AreaCode[]
  /** 既存入札の扱い。 add = 既存に追加、 replace = 既存 DRAFT を削除して再作成。 */
  mode: 'add' | 'replace'
  /**
   * 発注主体区分。 生成札全件に同一値で適用する (design `spot.html`
   * `#bblk-issuer-selector` と同じ)。 以前は発電に無言固定していた (issue #5366)。
   */
  issuerType: LicenseType
  /**
   * 排出係数紐付けの判別コメント完成形 (= `[特定契約{会社情報 ID}{英字}]`、
   * spec spot-bidding.md §7 AC-BSP-086)。 生成札全件の memo_jepx に同一値で
   * 格納する。 OFF のときは undefined。
   */
  memoJepx?: string
  /** メモ（自由記述、 社内限定）。 生成札全件の memo_free に同一値で格納する。 */
  memoFree?: string
}

/**
 * 一括作成フォームの確定値 (AC-005 の入力項目)。
 *
 * 数量の決め方は `volumeMode` を判別子とする union にしてある。 「フラグ +
 * 相互排他フィールド」 の形にすると、 使わない側のダミー値を呼び出し側が
 * 埋める必要があり、 生成側にも到達しない分岐と fallback が残るため。
 */
export type BulkCreateParams = BulkCreateCommonParams &
  (BulkCoverageVolume | BulkFixedVolume)

/** 生成された 1 札 (= 書込前の plain な値)。 */
export type BulkGeneratedBid = {
  areaCode: AreaCode
  timeCode: number
  direction: Side
  priceYenPerKwh: string
  volumeMwh: string
  /**
   * backend のプレビューが確認した手数料自動考慮の状態。 undefined は手数料単価を使えず OFF として
   * 扱う間で、 入札価格だけを送る。
   */
  fee?: BulkGeneratedBidFee
}

/** 札の手数料自動考慮の状態 (書込 contract に載せる組み合わせを 1 つの判別子にする)。 */
export type BulkGeneratedBidFee =
  /** ON。 `priceYenPerKwh` は backend が算出した入札価格、 送るのは希望価格と状態。 */
  | {
      kind: 'adjusted'
      desiredPriceYenPerKwh: string
      confirmed: FeeAutoConsiderConfirmed
    }
  /** 設定は確認できたが加減算しない (OFF)。 `priceYenPerKwh` が入力した入札価格。 */
  | { kind: 'notAdjusted'; confirmed: FeeAutoConsiderConfirmed }

/**
 * エリア × コマ × 方向の既存数量 1 件。 追加モードで差し引く既存入札量 (AC-BSP-005a、
 * `selectBulkAddExistingVolumes`) と、 判定母集合の既存札 (`buildBulkPreviewRows`) に使う。
 * `volumeMwh` はコマ別 MW の数値 (proto Decimal string を Number 化した値。
 * field 名が `Mwh` なのは proto field 名に合わせた歴史的経緯、 issue #4285)。
 */
export type ExistingBidVolume = {
  areaCode: AreaCode
  timeCode: number
  direction: Side
  volumeMwh: number
}

/**
 * 受渡日内の札から、 一括作成の「追加」 で差し引く既存入札量 (エリア × コマ × 売買の
 * コマ別 MW) を抜き出す (AC-BSP-005a)。
 *
 * - 最終結果になっていない札は入札量、 部分約定は約定量、 全約定は入札量 (= 約定量) を
 *   数える ([[BULK_ADD_COUNTED_CATEGORIES]]。 量の決め方は入札後ポジションの
 *   `unknownIssuerBidMw` と同じ)。 未約定・取消・失効は数えない
 * - 発注主体区分が空欄の取り込んだ札 (AC-BSP-111b) は数えない (AC-BSP-102 の ※)
 *
 * 冒頭の TODO(business-logic) の逸脱に含む (どの札を数えるかも本来 backend が決める)。
 *
 * 母集合は通常入札とブロック入札の両方 (どちらも同じエリア・コマ・売買の量を JEPX に
 * 出すため)。 下書きは絞り込み中 (AC-BSP-056) でも受渡日内の全申請セットを数える
 * (画面に出ていない下書きも再申請すれば出る量)。
 *
 * TODO(#10574): 発注主体区分で絞る (AC-BSP-005a「同じ発注主体区分の既存入札量」)。
 * 現状の調達必要量が発注主体区分ごとでないため、 差引も区分によらず合算している。
 */
export const selectBulkAddExistingVolumes = (
  spotBids: readonly SpotBidRow[],
  blockRequests: readonly BiddingBlockApprovalRequest[],
  isPastDeadline: boolean,
): ExistingBidVolume[] => {
  const spotVolumes = spotBids.flatMap((b): ExistingBidVolume[] => {
    if (b.issuerType === 'unknown') return []
    const category = deriveSpotBidStatusCategory(b, isPastDeadline)
    if (!BULK_ADD_COUNTED_CATEGORIES.has(category)) return []
    const mw =
      category === 'partialMatched' ? (b.execVolumeMwh ?? '0') : b.volumeMwh
    return [
      {
        areaCode: b.areaCode,
        timeCode: b.timeCode,
        direction: b.direction,
        volumeMwh: Number(mw),
      },
    ]
  })
  // ブロック入札は全 leg・全コマ約定 (all-or-nothing) なので、 全約定の約定量は入札量。
  const blockVolumes = expandBlockLegSlots(
    blockRequests.filter((g) => !isUnknownIssuerLicenseType(g.issuerType)),
    (g, leg) =>
      BULK_ADD_COUNTED_CATEGORIES.has(
        deriveBlockBidStatusCategory(
          {
            approvalState: g.approvalState,
            sendStatus: g.sendStatus,
            receiptChecking: g.receiptChecking,
            bidStatus: leg.bidStatus,
          },
          isPastDeadline,
        ),
      ),
  ).map((v) => ({ ...v, volumeMwh: Number(v.volumeMwh) }))
  return [...spotVolumes, ...blockVolumes]
}

/**
 * MW 数値を 0.1 MW 単位に **切り捨てる**。
 *
 * 数量の刻みは書込 contract に合わせる: backend の valueobject `BidVolumeMw`
 * (= go/internal/backend/domain/valueobject/bid_volume_mw.go) が「0.1 MW 単位
 * (小数 1 桁)」 以外を InvalidArgument で拒否し、DB 列も `numeric(10,1)`。
 * これは AC-BSP-050/053 の制度刻み (0.1 MW) そのものなので、 仕様どおりの札は
 * すべて永続化できる (issue #4217 / #4285)。
 *
 * 丸めは round ではなく **floor** にして need×カバー率 を超えないようにする
 * (= need 4.95MW×100% は 4.9MW。round だと 5.0MW で need 超過の札になる)。
 */
const floorTenthMw = (mw: number): number =>
  // 1e-9 は 2 進浮動小数の表現誤差対策 (= 8.7 が 86.99999999999999 になり 8.6 へ
  // 落ちるのを防ぐ)。0.05 MW の実端数は 1e-9 では吸収されないので floor が効く。
  Math.floor(mw * 10 + 1e-9) / 10

/** 既存入札を (エリア × コマ × 方向) キーで MW 合計する。 */
const existingKey = (
  areaCode: AreaCode,
  timeCode: number,
  direction: Side,
): string => `${areaCode}:${timeCode}:${direction}`

/**
 * 対象エリア 1 つ分の「そのコマの目標数量(MW)」解決子を、 数量の決め方から作る。
 *
 * - 戻り値が `undefined` = そのエリアには 1 件も生成しない。
 * - 解決子が `undefined` を返すコマ = そのコマには生成しない。
 *
 * 生成ループから モード固有の条件 (need の参照要否・方向フィルタ・カバー率の掛け算)
 * を追い出すためのもの。 モードは `volumeMode` の union で判別するので、
 * 「使わない側のフィールドの optional chaining + fallback」 が要らなくなる。
 */
const resolveTargetMwOfKoma = (
  params: BulkCreateParams,
  needByArea: ReadonlyMap<AreaCode, readonly number[]> | undefined,
  areaCode: AreaCode,
): ((koma: number) => number | undefined) | undefined => {
  if (params.volumeMode === 'fixed') {
    // 数量指定 (AC-BSP-005c): 全コマ同量。 need は参照しない (符号 = 不足 / 余剰 も
    // 見ない)。 need 未取得でも生成できる。
    const fixedMw = params.fixedVolumeMw
    return () => fixedMw
  }
  // 目標カバー率 (AC-BSP-005a): need Map に無いエリアは 1 件も生成しない
  // (need が無いまま生成すると札が silent に欠落する)。
  const needArr = needByArea?.get(areaCode)
  if (needArr === undefined) return undefined
  const pct = params.coverageRatePercent / 100
  return (koma) => {
    const needMw = needArr[koma - 1] ?? 0
    // 買い = 不足コマ (need>0) / 売り = 余剰コマ (need<0) のみ対象。数量は正の MW。
    if (params.side === Side.BUY && needMw > 0) return needMw * pct
    if (params.side === Side.SELL && needMw < 0) return -needMw * pct
    return undefined
  }
}

/**
 * 一括作成パラメータと per-area need (エリア別 48 コマ MW) から生成札を組み立てる
 * (AC-BSP-005a / 005c)。
 *
 * 共通:
 * - コマ範囲 [slotFrom, slotTo] (昇順正規化) の各エリア・各コマを対象にする。
 * - 数量は書込 contract の 0.1 MW グリッドへ切り捨てる (`floorTenthMw`。 切り捨て
 *   理由は同関数の godoc)。
 * - 追加モード (mode='add') は同エリア・同コマ・同方向の既存入札量 (MW。 最終結果に
 *   なっていない札の入札量と約定量) を差し引いた不足分のみ生成する。差引後 0 以下なら生成しない。
 *   置換モード (mode='replace') は既存を無視する。
 *
 * 目標カバー率モード (volumeMode='coverage'、 AC-BSP-005a):
 * - 数量 = |そのエリア・そのコマの need(MW)| × カバー率%。
 * - 買い (BUY) は need>0 (不足コマ)、売り (SELL) は need<0 (余剰コマ) のみを対象とし、
 *   対象外コマ・need 0 / 未取得コマ・need Map に無いエリアには札を生成しない。
 *
 * 数量指定モード (volumeMode='fixed'、 AC-BSP-005c):
 * - 対象コマ範囲の全コマへ同一の `fixedVolumeMw` を、 需給ギャップの方向とは無関係に
 *   `side` の方向で一律発注する。 need は参照しない (= need 未取得でも生成できる)。
 * - **対象エリアが単一でないときは 1 件も生成しない** (複数エリアへ同時に大量発注
 *   する事故の防止。 mock-app `EnsureBulkSingleArea()` の設計判断)。
 */
export const buildBulkSpotBids = (
  params: BulkCreateParams,
  needByArea: ReadonlyMap<AreaCode, readonly number[]> | undefined,
  existingBids: readonly ExistingBidVolume[] = [],
): BulkGeneratedBid[] => {
  if (params.areaCodes.length === 0) return []
  // 数量指定モードは単一エリア限定 (AC-BSP-005c)。2 エリア以上が渡ったら生成しない
  // (= UI 側で単一に絞るが、 契約としてここでも封じる)。
  if (params.volumeMode === 'fixed' && params.areaCodes.length > 1) return []

  const from = Math.min(params.slotFrom, params.slotTo)
  const to = Math.max(params.slotFrom, params.slotTo)

  // 追加モードの差引用に既存入札量 (MW) を (エリア × コマ × 方向) で合算する。
  const existingByKey = new Map<string, number>()
  if (params.mode === 'add') {
    for (const b of existingBids) {
      const key = existingKey(b.areaCode, b.timeCode, b.direction)
      existingByKey.set(key, (existingByKey.get(key) ?? 0) + b.volumeMwh)
    }
  }

  const out: BulkGeneratedBid[] = []
  for (const areaCode of params.areaCodes) {
    const targetMwOfKoma = resolveTargetMwOfKoma(params, needByArea, areaCode)
    if (targetMwOfKoma === undefined) continue
    for (let koma = from; koma <= to; koma++) {
      const targetMw = targetMwOfKoma(koma)
      if (targetMw === undefined) continue

      // need も札も MW。 0.1 MW グリッドへ切り捨てる (カバー率モードでは need 超過を
      // 作らない。 数量指定モードは入力自体が 0.1 刻みなので実質的に恒等)。
      const targetVolumeMw = floorTenthMw(targetMw)
      // 非有限 (NaN) は `<= 0` を素通りして数量 "NaN" の札になる。 モーダルの
      // `Number.isFinite` に頼らず、 page からも呼ばれる本関数の契約として弾く。
      if (!Number.isFinite(targetVolumeMw) || targetVolumeMw <= 0) continue

      let volumeMw = targetVolumeMw
      if (params.mode === 'add') {
        const existingMw =
          existingByKey.get(existingKey(areaCode, koma, params.side)) ?? 0
        volumeMw = floorTenthMw(targetVolumeMw - existingMw)
      }
      if (volumeMw <= 0) continue

      out.push({
        areaCode,
        timeCode: koma,
        direction: params.side,
        priceYenPerKwh: params.priceYenPerKwh,
        volumeMwh: volumeMw.toFixed(1),
      })
    }
  }
  return out
}

/**
 * 生成札の合計数量 (**MW**、 コマ別数量の単純合算)。 Step1 プレビュー表示用。
 *
 * AC-BSP-053 (2026-08-14 PdM 決定 B、 issue #4627) が「48 コマ分を足し合わせた合計欄も
 * MW (コマ別数量の単純合算値) とし MWh には換算しない。 対象は KPI フッター /
 * エリア×売買別集計テーブル / 申請内容サマリ / **一括作成プレビュー**」と定めるため、
 * `sumKomaMwToMwh` を通さない。 生成値も `volumeMwh` field も MW なので (issue #4285)
 * 単位換算は挟まない。
 *
 * 一括作成プレビュー以外の 3 箇所 (KPI フッター / エリア×売買別集計 / 申請内容サマリ)
 * は MWh 表示のまま残っており、 差し戻しは issue #6266 で追跡している。
 */
export const sumBulkVolumeMw = (bids: readonly BulkGeneratedBid[]): number =>
  bids.reduce((acc, b) => acc + Number(b.volumeMwh), 0)

/**
 * 生成札 1 行あたりの入札制限判定。
 *
 * `unknown` は「入札制限を取得できていないので判定が成立していない」状態 (= 制限が
 * 未設定で違反なしと確定した `ok` とは別物)。design `#bblk-preview-violation` の
 * 3 state (pending / ok / warn) の pending に対応する。
 */
export type BulkRowLimitStatus = 'ok' | 'violation' | 'unknown'

/** 行が違反している軸 (= バッジの意味範囲を表示で限定するために持つ)。 */
export type BulkRowViolationKind =
  | 'price-min'
  | 'price-max'
  | 'volume-max'
  | 'volume-total-max'
  | 'stairs'

/**
 * Step2 の 1 行 = 生成札そのもの + その行の判定結果。
 *
 * 判定を別配列で返すと index 対応が型で保証されず、範囲外 index が「OK」へ
 * fail-open する (レビュー指摘)。行に同梱して構造的に不可能にする。
 */
/** backend が価格範囲外と判定したエリアの、 算出した入札価格と理由 (AC-BSP-106)。 */
export type BulkFeeViolation = {
  orderPriceYenPerKwh: string
  violation: FeeAdjustmentViolation
}

export type BulkPreviewRow = BulkGeneratedBid & {
  status: BulkRowLimitStatus
  /** `status === 'violation'` のとき、その行が触れている違反軸。 */
  violationKinds: BulkRowViolationKind[]
  /**
   * `violationKinds` に `'volume-total-max'` を含むときの合計値/上限値 (AC-BSP-061
   * 規定文言のプレースホルダ用)。 バッジだけでなく実値入りの文言を該当項目直下に
   * 表示する (`BulkPreviewTable` 参照) ため、 判定結果に同梱する。
   */
  volumeTotalViolation?: VolumeTotalViolation
  /**
   * backend が返したエリア別の価格範囲外理由 (AC-BSP-106)。 表示専用で、 書込 contract には載せない。
   * 付いた行は `priceYenPerKwh` が backend の算出した入札価格。
   */
  feeAdjustmentViolation?: FeeAdjustmentViolation
}

const stairsKey = (
  areaCode: AreaCode,
  timeCode: number,
  direction: Side,
): string => `${timeCode}|${areaCode}|${direction}`

/**
 * 一括生成札を行単位で判定し、Step2 の表示行を組む
 * (design `#bblk-step2-body` / spec AC-BSP-005:273「各行に対して入札制限
 * (AC-BSP-021 の同一コマ×エリア×方向の階段数上限 / 上限数量) の判定結果を行単位で
 * インライン表示し、制限違反の行を確定前に検出できるようにする」)。
 *
 * 戻り値は `bids` と同じ順序・同じ長さ。
 *
 * 判定軸は下流 (承認申請ゲート `spotBidValidation.ts` / backend
 * `spot_bid.go ensureWithinBidLimits` + `StairsExcesses`) と同一に揃える:
 *
 * - **価格上下限** (`checkBidLimit` の price-min / price-max)
 * - **上限数量(1札)** (`BidKind.QTY`)。制限 QTY も札の数量も MW なので単位換算は挟まない
 *   (issue #4285 でコマ別数量は MW 建てに統一済み。 `volumeMwh` という field 名は
 *   proto / DB 列名に合わせた歴史的経緯で格納値は MW。 = `spotBidValidation.ts` の
 *   `detectLimitViolations` と同じ規則)。
 *
 *   ※ spec `bidding/bid-limits.md` §1-2 は上限数量を **上限数量(1札)** (手動起票の桁ミス
 *   防止ガード。自動起票の札には適用しない) と **上限数量(コマ計)** (起票由来を問わず
 *   総エクスポージャを捕捉、下の別項目) の 2 統制に分ける。 札は起票由来を保存するが、
 *   自動起票の免除が効くのは札の保存 (Create / Update) 時の判定だけで、 承認申請時の判定
 *   (`spotBidValidation.ts` / backend `ensureWithinBidLimits`) は起票由来を見ずに QTY (1札)
 *   を **全札に** 適用する。 プレビューは承認申請時の判定に揃えて全札に適用する
 *   (自動起票を免除すると「Step2 は OK なのに承認申請でブロック」 という確認画面と
 *   実 enforcement の乖離になる)。
 * - **上限数量(コマ計)** (`BidKind.QTY_TOTAL`、 AC-BSP-061 / AC-BSP-080)。 同一
 *   (エリア×コマ×売買) の 既存の有効札 (追加モードの既存 DRAFT 札 `existingBids` +
 *   既存の入札中・承認申請中・承認済の札 `existingActiveBids`) + 生成札 の合計を判定する
 *   (`detectVolumeTotalViolations`)。 起票由来を問わない総量統制なので、 上の QTY (1札)
 *   と異なり自動起票の免除は無い (bid-limits.md §1-2)。 `existingActiveBids` には
 *   有効なブロック入札の当該コマ数量も含めて渡す (AC-BSP-080、 `SpotBidding/index.tsx`)。
 * - **階段数上限** (AC-BSP-021)。承認申請ゲートと同じ `detectStairsExcesses` /
 *   `JEPX_STAIRS_LIMIT` を再利用する (判定の二重実装をしない)。母集合は
 *   「追加モードなら既存 DRAFT 札 + 生成札」「置換モードなら生成札のみ」
 *   (置換は対象範囲の既存 DRAFT を消してから入れるため。`SpotBidding/index.tsx` の
 *   `isReplaced` と同じ前提)。階段数は settings の入札制限に依存しない JEPX 制度上限
 *   なので、`limits === undefined` でも判定できる。
 *
 * `limits === undefined` は「入札制限を取得できていない」(= 取得中 / 取得失敗) を表し、
 * 制限依存の軸を判定できないので `unknown` を返す。空 `BidLimits` を渡すと
 * 「全エリア制限未設定 = 違反なし」と区別が付かず未判定を `ok` と誤表示するため、
 * 呼び出し側は取得できていない間は必ず `undefined` を渡す。
 *
 * **この結果は原則として表示専用**: 書込 (DRAFT 作成) の可否ゲートには使わない。 spec 上
 * ブロックは承認申請時 (AC-BSP-008) の要件で、AC-BSP-005 は「確定前に検出できるように
 * する」= 検出要件。
 *
 * 唯一の例外が 数量指定モード (AC-BSP-005c) の `volume-max`。 この経路の札は
 * `origin = BULK_FIXED_VOLUME` で送られ、backend の `SpotBidUsecase.Create` が
 * 上限数量(1札) ゲート (AC-BSP-103) で必ず拒否する = 対応する検証点が backend にあるため、
 * `BulkCreateModal` は確定ボタンを止める (#8324 の EN 暫定判断)。 目標カバー率モード
 * (`BULK_TARGET_COVERAGE`) は自動起票として同ゲートから除外されるので止めない。
 *
 * @param existingBids - 追加モードの既存 DRAFT 札 (階段数 / 上限数量(コマ計) の母集合。
 *   差引の母集合 `selectBulkAddExistingVolumes` とは別)
 * @param existingActiveBids - 上限数量(コマ計) (AC-BSP-061 / AC-BSP-080) の合算母集合に
 *   加える 既存の有効札 (入札中・承認申請中・承認済。 ブロック入札の当該コマ数量を含む)。 `existingBids` (下書き) とは別軸
 *   なので mode に関わらず常に合算する (= 置換モードでも承認申請中以降の札は消えない)。
 * @param feeViolationByArea - backend が価格範囲外と判定したエリアの理由 (AC-BSP-106。 表示専用)
 */
export const buildBulkPreviewRows = (
  bids: readonly BulkGeneratedBid[],
  limits: BidLimits | undefined,
  mode: BulkCreateParams['mode'] = 'add',
  existingBids: readonly ExistingBidVolume[] = [],
  existingActiveBids: readonly ExistingBidVolume[] = [],
  feeViolationByArea: ReadonlyMap<AreaCode, BulkFeeViolation> = new Map(),
): BulkPreviewRow[] => {
  // 階段数の母集合 (AC-BSP-021)。承認申請ゲート `detectStairsExcesses(draftBids)` と
  // 同じ「札 1 件 = 1 entry」の集合を作る。
  const stairsBase = mode === 'add' ? existingBids : []
  const excessKeys = new Set(
    detectStairsExcesses(
      [...stairsBase, ...bids].map((b) => ({
        areaCode: b.areaCode,
        timeCode: b.timeCode,
        direction: b.direction,
      })),
    ).map((e) => stairsKey(e.areaCode, e.timeCode, e.side)),
  )

  // 上限数量(コマ計) の母集合 (AC-BSP-061 / AC-BSP-080)。 置換モードは対象範囲の既存
  // DRAFT を削除してから作るため `existingBids` は含めない (= stairsBase と同じ規則)。
  // 既存の有効札 (`existingActiveBids`) は下書きではないので mode に関わらず常に含める。
  const volumeTotalBase = mode === 'add' ? existingBids : []
  const volumeTotalPopulation = [
    ...volumeTotalBase.map((b) => ({ ...b, volumeMwh: String(b.volumeMwh) })),
    ...existingActiveBids.map((b) => ({
      ...b,
      volumeMwh: String(b.volumeMwh),
    })),
    ...bids,
  ]
  const volumeTotalByKey =
    limits === undefined
      ? new Map<string, VolumeTotalViolation>()
      : new Map(
          detectVolumeTotalViolations(volumeTotalPopulation, limits).map(
            (v) => [stairsKey(v.areaCode, v.timeCode, v.side), v],
          ),
        )

  return bids.map((bid): BulkPreviewRow => {
    // 範囲外のエリアの行は、 backend の算出した入札価格と理由を表示用に載せる (書込には載せない)。
    // 入札制限の判定も表示と同じ入札価格で行う (希望価格で判定すると、 表示と判定が食い違う)。
    const fee = feeViolationByArea.get(bid.areaCode)
    const b =
      fee === undefined
        ? bid
        : {
            ...bid,
            priceYenPerKwh: fee.orderPriceYenPerKwh,
            feeAdjustmentViolation: fee.violation,
          }
    const violationKinds: BulkRowViolationKind[] = []
    if (excessKeys.has(stairsKey(b.areaCode, b.timeCode, b.direction))) {
      violationKinds.push('stairs')
    }
    const volumeTotalViolation = volumeTotalByKey.get(
      stairsKey(b.areaCode, b.timeCode, b.direction),
    )
    if (limits === undefined) {
      // 制限依存の軸は判定できないが、階段数は判定できる。 判定できた違反まで
      // 「未判定」に丸めない (= 分かっている違反を隠さない)。
      return {
        ...b,
        status: violationKinds.length > 0 ? 'violation' : 'unknown',
        violationKinds,
      }
    }
    const limit = resolveAreaSideLimit(limits, b.areaCode, b.direction)
    violationKinds.push(
      // 制限 QTY も札の数量も MW (issue #4285)。 単位換算は挟まない。
      ...checkBidLimit(Number(b.priceYenPerKwh), Number(b.volumeMwh), limit),
    )
    if (volumeTotalViolation !== undefined) {
      violationKinds.push('volume-total-max')
    }
    return {
      ...b,
      status: violationKinds.length > 0 ? 'violation' : 'ok',
      violationKinds,
      volumeTotalViolation,
    }
  })
}

/**
 * Step2 の行単位判定をエリアへ集約し、違反を含むエリアを返す
 * (design (C)、AC-005 の入札制限チェック)。Step1 のエリア集約 warning 表示用。
 *
 * **判定基準そのものを持たない** (= `buildBulkPreviewRows` の結果を数えるだけ)。Step1
 * (集約警告) と Step2 (行単位バッジ) が別基準になると「Step1 では範囲外と警告されるのに
 * Step2 では全行 OK で確定できる」矛盾が起きるため、evaluator を 1 本に固定する。
 */
export const detectBulkLimitViolations = (
  rows: readonly BulkPreviewRow[],
): AreaCode[] => {
  const violating = new Set<AreaCode>()
  for (const r of rows) {
    if (r.status === 'violation') violating.add(r.areaCode)
  }
  return [...violating]
}
